/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Theme,
  Typography,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import { X } from "@jda/lui-common-icon-library-mui5";

import { useStyles } from "./styles";
import Loader from "../loader/Loader";

export interface IDialog {
  dialogTitle: string;
  dialogContentText: string;
  recordName: string;
  primaryButtonText?: string;
  secondButtonText?: string;
  handleOnCancel: Function;
  handleOnOk: Function;
  showDialogLoader: boolean;
}

const DialogTitleStyled = (props: any) => {
  const { children } = props;
  return (
    <DialogTitle>
      <Box display="flex" alignItems="center">
        <Box flexGrow={1}>
          <Typography variant="h5">{children}</Typography>
        </Box>
      </Box>
    </DialogTitle>
  );
};

const DialogContentStyled = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(DialogContent) as typeof DialogContent;

const DialogActionsStyled = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    background: "transparent",
    paddingTop: `0px`,
    paddingBottom: `0px`,
    justifyContent: "flex-end",
    "& button": {
      marginLeft: `${theme.spacing(2.25)}px !Important`,
    },
  },
}))(DialogActions) as typeof DialogActions;

export const AlertDialog = (props: IDialog) => {
  const classes = useStyles();
  return (
    <Paper elevation={0} square>
      <Box>
        <div>
          <Dialog
            id="dial"
            open={true}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            classes={{ scrollPaper: classes.scrollPaper }}
            BackdropProps={{
              classes: {
                root: classes.root,
              },
            }}
            PaperProps={{
              classes: {
                root: classes.paper,
              },
            }}
          >
            <DialogTitleStyled id="alert-dialog-title">
              <Box>
                <Box className={classes.bold}>{props.dialogContentText}</Box>
                <IconButton
                  size="small"
                  id="closeBox"
                  aria-label="close"
                  classes={{ root: classes.closeBtn }}
                  onClick={() => {
                    props.handleOnCancel();
                  }}
                >
                  {" "}
                  <X />
                </IconButton>
              </Box>
            </DialogTitleStyled>
            <Divider />
            <DialogContentStyled>
              <Typography
                component="span"
                gutterBottom
                id="alert-dialog-description"
              >
                {props.dialogTitle}
                <Typography component="span" className={classes.bold}>
                  {props.recordName}
                </Typography>
                ?
              </Typography>
              <br></br>
              <Typography>This action cannot be undone.</Typography>
            </DialogContentStyled>
            <Divider />
            <DialogActionsStyled>
              <Loader showLoader={props.showDialogLoader} isCenter />
              <Button
                variant="outlined"
                onClick={() => {
                  props.handleOnCancel();
                }}
                color="primary"
                id="buttonCancel"
              >
                {props.secondButtonText ? props.secondButtonText : "Disagree"}
              </Button>
              <Button
                id="okBtn"
                className={`${classes.buttonStyle} ${classes.deleteButtonStyler}`}
                onClick={() => props.handleOnOk()}
                variant="contained"
                disableElevation
                disabled={props.showDialogLoader}
              >
                {props.primaryButtonText ? props.primaryButtonText : "Agree"}
              </Button>
            </DialogActionsStyled>
          </Dialog>
        </div>
      </Box>
    </Paper>
  );
};
